'use client';

import { Card, HeadingItem } from '@terra/ui-design-system';
import { Box, Grid, HStack } from '@terra/ui-styled-system';
import type { MouseEvent, ReactNode } from 'react';

export type DashboardStripCell = {
  key: string;
  content: ReactNode;
  onClick?: () => void;
  onContextMenu?: (event: MouseEvent<HTMLElement>) => void;
};

type DashboardStripCardProps = {
  title: string;
  trailingContent?: ReactNode;
  columns: 4 | 5;
  cells: DashboardStripCell[];
};

export function DashboardStripCard({
  title,
  trailingContent,
  columns,
  cells,
}: DashboardStripCardProps) {
  return (
    <Card.Root
      w="full"
      flexShrink={0}
      boxShadow="none"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.muted"
      overflow="hidden"
    >
      <HStack
        px={4}
        py={3}
        gap={4}
        justifyContent="space-between"
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor="border.neutral.subtle"
      >
        <HeadingItem color="fg.neutral.default">{title}</HeadingItem>
        {trailingContent}
      </HStack>
      <Grid
        columns={columns === 5 ? { base: 2, lg: 5 } : { base: 2, lg: 4 }}
        gap={0}
      >
        {cells.map((cell, index) => (
          <Box
            key={cell.key}
            px={4}
            py={4}
            minW={0}
            borderLeftWidth={{
              base: index % 2 === 0 ? 0 : '1px',
              lg: index === 0 ? 0 : '1px',
            }}
            borderTopWidth={{ base: index >= 2 ? '1px' : 0, lg: 0 }}
            borderStyle="solid"
            borderColor="border.neutral.muted"
            cursor={cell.onClick ? 'pointer' : 'default'}
            _hover={cell.onClick ? { bg: 'bg.neutral.hover' } : undefined}
            transition="background-color 0.2s ease-in-out"
            onClick={cell.onClick}
            onContextMenu={cell.onContextMenu}
          >
            {cell.content}
          </Box>
        ))}
      </Grid>
    </Card.Root>
  );
}
